import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiMail,
  FiMapPin,
  FiPackage,
  FiPhone,
  FiRefreshCw,
  FiXCircle,
  FiUser,
} from "react-icons/fi";
import Button from "src/components/Button/Button";
import PageHeader from "src/components/pageHeader";
import PageLoading from "src/components/pageLoading";
import { displayDate } from "src/utils/dateHandler.util";
import {
  useGetKarmaExchangeByIdQuery,
  useUpdateRewardExchangeStatusMutation,
} from "../karma-exchanges.service";
import {
  defaultExchangeStatus,
  ExchangeStatus,
  getCategoryLabel,
  getExchangeProviderName,
  getRewardCategory,
  getStatusHint,
  getStatusPresentation,
  getStatusStepText,
  isPhysicalExchange,
  isTremendousDigitalExchange,
  isTremendousPendingApproval,
} from "../utils/karma-exchange-ui";

const statusIconMap: Record<ExchangeStatus, typeof FiClock> = {
  PENDING: FiClock,
  PROCESSING: FiRefreshCw,
  SHIPPED: FiPackage,
  DELIVERED: FiCheckCircle,
  REJECTED: FiXCircle,
};

type StatusFeedback = {
  type: "success" | "error";
  message: string;
};

const InfoField = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: typeof FiUser;
}) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <div className="mt-2 flex items-start gap-2 text-sm font-medium text-slate-800">
      {Icon ? <Icon className="mt-0.5 h-4 w-4 text-slate-400" /> : null}
      <span className="break-words">{value || "-"}</span>
    </div>
  </div>
);

const KarmaExchangeDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: exchange, isLoading, isError } = useGetKarmaExchangeByIdQuery(id || "", {
    skip: !id,
  });
  const [updateRewardExchangeStatus] = useUpdateRewardExchangeStatusMutation();

  const [updatingStatus, setUpdatingStatus] = React.useState(false);
  const [pendingStatus, setPendingStatus] = React.useState<ExchangeStatus | null>(null);
  const [statusFeedback, setStatusFeedback] = React.useState<StatusFeedback | null>(null);
  const [optimisticStatus, setOptimisticStatus] = React.useState<ExchangeStatus | null>(null);

  React.useEffect(() => {
    setOptimisticStatus(null);
  }, [exchange?.status]);

  const updateExchangeStatus = async (status: ExchangeStatus) => {
    if (!exchange || !id) return;

    const tremendousDigital = isTremendousDigitalExchange(exchange);
    if (tremendousDigital && status !== "PROCESSING" && status !== "REJECTED") {
      return;
    }

    setStatusFeedback(null);
    setUpdatingStatus(true);
    setPendingStatus(status);

    try {
      await updateRewardExchangeStatus({
        exchangeId: id,
        newStatus: status,
      }).unwrap();

      setOptimisticStatus(status);
      setStatusFeedback({
        type: "success",
        message:
          tremendousDigital && status === "PROCESSING"
            ? "Exchange approved. Tremendous order has been requested and is now processing."
            : `Exchange status updated to ${getStatusPresentation(status).label}.`,
      });
    } catch (err) {
      console.error("Error updating status:", err);
      setStatusFeedback({
        type: "error",
        message: "We couldn't update the exchange status. Please try again.",
      });
    } finally {
      setUpdatingStatus(false);
      setPendingStatus(null);
    }
  };

  if (isLoading) {
    return <PageLoading />;
  }

  if (!id || isError || !exchange) {
    return <Navigate to="/505" />;
  }

  const currentStatus = optimisticStatus || exchange.status || defaultExchangeStatus;
  const exchangeWithCurrentStatus = { ...exchange, status: currentStatus };
  const providerName = getExchangeProviderName(exchange);
  const rewardCategory = getRewardCategory(exchange);
  const isPhysical = isPhysicalExchange(exchange);
  const isTremendousDigital = isTremendousDigitalExchange(exchange);
  const isStatusManagedExchange = isPhysical || isTremendousDigital;
  const categoryLabel = getCategoryLabel(rewardCategory);
  const statusPresentation = getStatusPresentation(currentStatus, {
    isTremendousPendingApproval: isTremendousPendingApproval(exchangeWithCurrentStatus),
  });
  const statusHint = getStatusHint(currentStatus, {
    isPhysicalExchange: isPhysical,
    isTremendousDigitalExchange: isTremendousDigital,
  });
  const statusStepText = getStatusStepText(currentStatus, {
    isTremendousDigitalExchange: isTremendousDigital,
  });
  const StatusIcon = statusIconMap[currentStatus];

  const address = exchange.address || null;
  const addressSummary = [address?.city, address?.state, address?.postalCode]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="page_container space-y-4">
      <PageHeader text="Karma Exchange Details" />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Exchange</p>
              <h1 className="mt-2 text-2xl font-semibold text-slate-900">
                {exchange.reward?.name || "Reward exchange"}
              </h1>
              <p className="mt-1 font-mono text-xs text-slate-500">ID: {exchange.id}</p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 md:max-w-sm">
              <p className="text-xs uppercase tracking-wide text-slate-500">Current Status</p>
              <div
                className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${statusPresentation.badgeClasses}`}
              >
                <StatusIcon className="h-4 w-4" />
                {statusPresentation.label}
              </div>
              <p className="mt-2 text-sm text-slate-600">{statusHint}</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <InfoField label="Category" value={categoryLabel} />
            <InfoField label="Provider" value={providerName} />
            <InfoField label="Amount" value={`${exchange.amount} Karma`} />
            <InfoField
              label="Created"
              value={exchange.createdAt ? displayDate(exchange.createdAt) : "-"}
            />
          </div>
        </section>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Customer Details</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Recipient and account information for this exchange.
                  </p>
                </div>
                {exchange.userId && (
                  <Link
                    to={`/users/${exchange.userId}`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 no-underline transition-colors hover:bg-slate-100 hover:text-slate-900"
                  >
                    <FiExternalLink className="h-3.5 w-3.5" />
                    View User Profile
                  </Link>
                )}
              </div>

              {exchange.user && (
                <div className="mt-4 flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  {exchange.user.pictureURL ? (
                    <img
                      src={exchange.user.pictureURL}
                      alt={exchange.user.name}
                      className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm"
                    />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-slate-200 ring-2 ring-white shadow-sm">
                      <FiUser className="h-6 w-6 text-slate-500" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-slate-900">{exchange.user.name}</p>
                    {exchange.user.email && (
                      <p className="mt-0.5 truncate text-sm text-slate-500">{exchange.user.email}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <InfoField label="Name" value={exchange.user?.name || "-"} icon={FiUser} />
                <InfoField label="Email" value={exchange.user?.email || "-"} icon={FiMail} />
                <InfoField
                  label="Phone Number"
                  value={
                    exchange.user?.phoneNumber ||
                    exchange.user?.appProfile?.phoneNumber ||
                    "-"
                  }
                  icon={FiPhone}
                />
              </div>
            </section>

            {(exchange.voucher || exchange.expiredIn) && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Redemption Details</h2>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {exchange.voucher && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Voucher Code</p>
                      <p className="mt-2 break-all rounded-md bg-slate-900 px-3 py-2 font-mono text-sm text-white">
                        {exchange.voucher}
                      </p>
                    </div>
                  )}

                  {exchange.expiredIn && (
                    <InfoField
                      label="Expires In"
                      value={displayDate(exchange.expiredIn)}
                      icon={FiClock}
                    />
                  )}
                </div>
              </section>
            )}

            {isPhysical && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Delivery Address</h2>
                    <p className="mt-1 text-sm text-slate-500">Shipping destination saved by the user.</p>
                  </div>
                  {addressSummary ? (
                    <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                      {addressSummary}
                    </span>
                  ) : null}
                </div>

                {address ? (
                  <>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Street Address</p>
                      <p className="mt-2 text-sm font-medium text-slate-800">{address.streetAddress}</p>
                      {address.additionalDetails ? (
                        <p className="mt-1 text-sm text-slate-600">{address.additionalDetails}</p>
                      ) : null}
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      <InfoField label="Recipient" value={address.fullName} icon={FiUser} />
                      <InfoField label="Phone Number" value={address.phoneNumber} icon={FiPhone} />
                      <InfoField label="Country Code" value={address.countryCode} icon={FiMapPin} />
                      <InfoField label="Postal Code" value={address.postalCode} icon={FiPackage} />
                      <InfoField label="State" value={address.state} icon={FiMapPin} />
                      <InfoField label="City" value={address.city} icon={FiMapPin} />
                    </div>
                  </>
                ) : (
                  <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                    This exchange is marked as physical, but no delivery address is available.
                  </div>
                )}
              </section>
            )}
          </div>

          <div className="space-y-6">
            {isStatusManagedExchange ? (
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Exchange Status</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {isTremendousDigital
                    ? "Review pending Tremendous requests and trigger fulfillment approval from the dashboard."
                    : "Physical exchanges must be reviewed before fulfillment, then marked as delivered when completed."}
                </p>

                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Current Lifecycle Step</p>
                  <div
                    className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold ${statusPresentation.badgeClasses}`}
                  >
                    <StatusIcon className="h-4 w-4" />
                    {statusPresentation.label}
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{statusStepText}</p>
                </div>

                {statusFeedback ? (
                  <div
                    className={`mt-4 rounded-xl px-4 py-3 text-sm font-medium ${
                      statusFeedback.type === "success"
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : "bg-red-50 text-red-700 ring-1 ring-red-200"
                    }`}
                  >
                    {statusFeedback.message}
                  </div>
                ) : null}

                {currentStatus === "PENDING" ? (
                  <div className="mt-5 space-y-3">
                    <p className="text-sm font-medium text-slate-700">
                      {isTremendousDigital ? "Review the digital request" : "Review the request"}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        type="button"
                        onClick={() => updateExchangeStatus("PROCESSING")}
                        loading={updatingStatus && pendingStatus === "PROCESSING"}
                        loadingText={isTremendousDigital ? "Approving..." : "Accepting..."}
                        disabled={updatingStatus}
                      >
                        {isTremendousDigital ? "Approve Request" : "Accept Exchange"}
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => updateExchangeStatus("REJECTED")}
                        loading={updatingStatus && pendingStatus === "REJECTED"}
                        loadingText="Rejecting..."
                        disabled={updatingStatus}
                      >
                        Reject Exchange
                      </Button>
                    </div>
                  </div>
                ) : null}

                {currentStatus === "PROCESSING" ? (
                  <div className="mt-5">
                    <p className="mb-3 text-sm font-medium text-slate-700">
                      {isTremendousDigital ? "Fulfillment in progress" : "Complete fulfillment"}
                    </p>

                    {isTremendousDigital ? (
                      <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800">
                        Admin approval is complete. This request will automatically move to delivered when Tremendous sends a successful webhook event.
                      </div>
                    ) : (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-sm text-emerald-800">
                          The exchange has already been accepted. Once the shipment is completed, mark it as delivered.
                        </p>
                        <div className="mt-4 flex justify-end">
                          <Button
                            type="button"
                            onClick={() => updateExchangeStatus("DELIVERED")}
                            loading={updatingStatus && pendingStatus === "DELIVERED"}
                            loadingText="Updating..."
                            disabled={updatingStatus}
                          >
                            Mark as Delivered
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {currentStatus === "SHIPPED" && !isTremendousDigital ? (
                  <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-sm text-emerald-800">
                      Shipment is marked in transit. Mark as delivered once the user receives the reward.
                    </p>
                    <div className="mt-4 flex justify-end">
                      <Button
                        type="button"
                        onClick={() => updateExchangeStatus("DELIVERED")}
                        loading={updatingStatus && pendingStatus === "DELIVERED"}
                        loadingText="Updating..."
                        disabled={updatingStatus}
                      >
                        Mark as Delivered
                      </Button>
                    </div>
                  </div>
                ) : null}

                {(currentStatus === "DELIVERED" || currentStatus === "REJECTED") ? (
                  <div
                    className={`mt-5 rounded-xl border p-4 text-sm ${
                      currentStatus === "DELIVERED"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-rose-200 bg-rose-50 text-rose-800"
                    }`}
                  >
                    {currentStatus === "DELIVERED"
                      ? isTremendousDigital
                        ? "This Tremendous request is delivered. The learner can now claim the reward."
                        : "This physical exchange is complete and no further status changes are available."
                      : isTremendousDigital
                        ? "This Tremendous request was rejected and no further status changes are available."
                        : "This physical exchange was rejected and no further status changes are available."}
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KarmaExchangeDetailsPage;
